import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';

interface SystemStatus {
    mode: 'shared' | 'vps';
    redis_ok: boolean;
    redis_info: string | null;
    drivers: { cache: string; session: string; queue: string };
    php_version: string;
    laravel_version: string;
    can_write_env: boolean;
    test_real_limits_on_local: boolean;
    app_env?: string;
}

const API = import.meta.env.VITE_API_URL || '/api';
const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
});

// ─── Status Badge ────────────────────────────────────────────────────────────
function Badge({ ok, text }: { ok: boolean; text: string }) {
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
            ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
        }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
            {text}
        </span>
    );
}

// ─── Driver Row ───────────────────────────────────────────────────────────────
function DriverRow({ label, value, vpsValue }: { label: string; value: string; vpsValue: string }) {
    const isVps = value === vpsValue;
    return (
        <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
            <span className="text-sm text-gray-600">{label}</span>
            <div className="flex items-center gap-2">
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">{value}</code>
                {isVps
                    ? <span className="text-xs text-purple-600 font-medium">VPS ✓</span>
                    : <span className="text-xs text-gray-400">Shared</span>
                }
            </div>
        </div>
    );
}

// ─── Plan Colors Map ──────────────────────────────────────────────────────────
const planColors: Record<string, { label: string; text: string; bg: string; icon: string }> = {
    free: { label: 'Dùng thử', text: 'Free Trial', bg: 'bg-gray-100 text-gray-700 border-gray-200', icon: '🌱' },
    flash_3d: { label: 'Flash 3 ngày', text: 'Flash Plan', bg: 'bg-teal-50 text-teal-700 border border-teal-200', icon: '⚡' },
    mini_7d: { label: 'Mini 7 ngày', text: 'Mini Plan', bg: 'bg-green-50 text-green-700 border border-green-200', icon: '📦' },
    event_15d: { label: 'Sự kiện 15 ngày', text: 'Event Plan', bg: 'bg-amber-50 text-amber-700 border-amber-150', icon: '🎉' },
    brand_30d: { label: 'Thương hiệu', text: 'Brand Plan', bg: 'bg-purple-50 text-purple-700 border-purple-150', icon: '👑' },
    agency_30d: { label: 'Agency', text: 'Agency Plan', bg: 'bg-rose-50 text-rose-700 border-rose-150', icon: '🏢' },
};

// ─── Asset meta info ──────────────────────────────────────────────────────────
const ASSET_META: Record<string, { label: string; icon: string; hint: string; maxKb: number; aspect?: string }> = {
    default_icon:            { label: 'Icon / Logo mặc định', icon: '🏷️', hint: 'PNG/WebP · Tối đa 1MB · Khuyến nghị 256×256px', maxKb: 1024, aspect: '1:1' },
    watermark:               { label: 'Watermark mặc định', icon: '💧', hint: 'PNG trong suốt · Tối đa 512KB · Khuyến nghị 400×200px', maxKb: 512, aspect: '2:1' },
    default_bg_lucky_wheel:  { label: 'Background — Lucky Wheel', icon: '🎡', hint: 'JPG/WebP · Tối đa 3MB · Khuyến nghị 1920×1080px', maxKb: 3072, aspect: '16:9' },
    default_bg_flip_card:    { label: 'Background — Flip Card', icon: '🃏', hint: 'JPG/WebP · Tối đa 3MB · Khuyến nghị 1920×1080px', maxKb: 3072, aspect: '16:9' },
    default_bg_scratch_card: { label: 'Background — Scratch Card', icon: '🎟️', hint: 'JPG/WebP · Tối đa 3MB · Khuyến nghị 1920×1080px', maxKb: 3072, aspect: '16:9' },
    default_bg_egg_pick:     { label: 'Background — Egg Pick', icon: '🥚', hint: 'JPG/WebP · Tối đa 3MB · Khuyến nghị 1920×1080px', maxKb: 3072, aspect: '16:9' },
    default_card_back_flip_card: { label: 'Mặt sau bài — Flip Card', icon: '🎴', hint: 'JPG/WebP · Tối đa 1MB · Khuyến nghị 300×420px', maxKb: 1024, aspect: '5:7' },
};

interface SystemAssetItem {
    key: string;
    label: string;
    value: string | null;
    size_kb: number;
    updated_by: string | null;
    updated_at: string | null;
}

export default function SystemSettings() {
    const [activeTab, setActiveTab] = useState<'infra' | 'tenants' | 'plans' | 'assets'>('tenants');
    const [status, setStatus]   = useState<SystemStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy]       = useState(false);
    const [toast, setToast]     = useState<{ msg: string; ok: boolean } | null>(null);
    const [testResult, setTestResult] = useState<{ msg: string; ok: boolean } | null>(null);

    // Plans Management
    const [plans, setPlans] = useState<Record<string, any> | null>(null);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansSaving, setPlansSaving] = useState(false);

    // Redis form
    const [redisHost, setRedisHost]   = useState('127.0.0.1');
    const [redisPort, setRedisPort]   = useState('6379');
    const [redisPass, setRedisPass]   = useState('');

    // Tenants Management
    const [tenants, setTenants] = useState<any[]>([]);
    const [tenantsLoading, setTenantsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Modal Form Fields
    const [targetPlanCode, setTargetPlanCode] = useState('free');
    const [durationDays, setDurationDays] = useState('30');
    const [notes, setNotes] = useState('Kích hoạt thủ công bởi Super Admin');

    // Points Management Modal
    const [pointModalOpen, setPointModalOpen] = useState(false);
    const [pointAmount, setPointAmount] = useState(100);
    const [pointType, setPointType] = useState<'bonus' | 'topup'>('bonus');
    const [pointDesc, setPointDesc] = useState('Thưởng điểm hệ thống (Super Admin)');

    // System Assets
    const [assets, setAssets] = useState<SystemAssetItem[]>([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);
    const [resetingKey, setResetingKey] = useState<string | null>(null);

    // Cài đặt email OTP
    const [otpEmail, setOtpEmail] = useState('');
    const [isEditingOtpEmail, setIsEditingOtpEmail] = useState(false);
    const [newOtpEmail, setNewOtpEmail] = useState('');
    const [otpEmailStep, setOtpEmailStep] = useState<1 | 2>(1);
    const [otpEmailVal, setOtpEmailVal] = useState('');
    const [sendingOtpEmail, setSendingOtpEmail] = useState(false);
    const [confirmingOtpEmail, setConfirmingOtpEmail] = useState(false);

    // Reset dữ liệu theo chiến dịch
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [resetTenantCampaigns, setResetTenantCampaigns] = useState<any[]>([]);
    const [resetTenantLoading, setResetTenantLoading] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [otpResetSent, setOtpResetSent] = useState(false);
    const [otpResetVal, setOtpResetVal] = useState('');
    const [sendingOtpReset, setSendingOtpReset] = useState(false);
    const [confirmingOtpReset, setConfirmingOtpReset] = useState(false);

    // Reset toàn bộ cửa hàng (Tenant)
    const [tenantResetModalOpen, setTenantResetModalOpen] = useState(false);
    const [otpTenantResetSent, setOtpTenantResetSent] = useState(false);
    const [otpTenantResetVal, setOtpTenantResetVal] = useState('');
    const [sendingOtpTenantReset, setSendingOtpTenantReset] = useState(false);
    const [confirmingTenantReset, setConfirmingTenantReset] = useState(false);

    const showToast = (msg: string, ok: boolean) => {
        setToast({ msg, ok });
        setTimeout(() => setToast(null), 5000);
    };

    const loadStatus = async () => {
        try {
            const res  = await fetch(`${API}/admin/system/status`, { headers: headers() });
            const data = await res.json();
            setStatus(data);
        } catch {
            showToast('Không thể tải thông tin hệ thống', false);
        } finally {
            setLoading(false);
        }
    };

    const loadTenants = async (search = '') => {
        setTenantsLoading(true);
        try {
            const url = search 
                ? `${API}/super/tenants?search=${encodeURIComponent(search)}`
                : `${API}/super/tenants`;
            const res = await fetch(url, { headers: headers() });
            const data = await res.json();
            if (data.success) {
                setTenants(data.data);
            } else {
                showToast(data.message || 'Không thể tải danh sách cửa hàng', false);
            }
        } catch {
            showToast('Không thể kết nối API quản trị cửa hàng', false);
        } finally {
            setTenantsLoading(false);
        }
    };

    const loadPlans = async () => {
        setPlansLoading(true);
        try {
            const res = await fetch(`${API}/super/plans`, { headers: headers() });
            const data = await res.json();
            if (data.success) {
                setPlans(data.data);
            } else {
                showToast(data.message || 'Không thể tải danh sách gói cước', false);
            }
        } catch {
            showToast('Không thể kết nối API cấu hình gói cước', false);
        } finally {
            setPlansLoading(false);
        }
    };

    const handleSavePlans = async () => {
        setPlansSaving(true);
        try {
            const res = await fetch(`${API}/super/plans`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ plans }),
            });
            const data = await res.json();
            if (data.success) {
                showToast('Đã lưu cấu hình gói cước thành công!', true);
            } else {
                showToast(data.message || 'Lỗi khi lưu cấu hình', false);
            }
        } catch {
            showToast('Lỗi kết nối khi lưu gói cước', false);
        } finally {
            setPlansSaving(false);
        }
    };

    const loadAssets = async () => {
        setAssetsLoading(true);
        try {
            const res = await fetch(`${API}/admin/system/assets`, { headers: headers() });
            const data = await res.json();
            if (data.success) setAssets(data.data);
            else showToast(data.message || 'Không thể tải assets', false);
        } catch {
            showToast('Lỗi kết nối khi tải system assets', false);
        } finally {
            setAssetsLoading(false);
        }
    };

    const handleUploadAsset = async (key: string, file: File) => {
        setUploadingKey(key);
        try {
            const form = new FormData();
            form.append('image', file);
            form.append('key', key);
            const res = await fetch(`${API}/admin/system/assets/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
                body: form,
            });
            const data = await res.json();
            if (data.success) {
                showToast(data.message, true);
                await loadAssets();
            } else {
                showToast(data.error?.message || 'Upload thất bại', false);
            }
        } catch {
            showToast('Lỗi khi upload asset', false);
        } finally {
            setUploadingKey(null);
        }
    };

    const handleResetAsset = async (key: string, label: string) => {
        if (!window.confirm(`Reset "${label}" về ảnh mặc định hệ thống?`)) return;
        setResetingKey(key);
        try {
            const res = await fetch(`${API}/admin/system/assets/${key}`, {
                method: 'DELETE',
                headers: headers(),
            });
            const data = await res.json();
            if (data.success) {
                showToast(data.message, true);
                await loadAssets();
            } else {
                showToast(data.message || 'Lỗi reset asset', false);
            }
        } catch {
            showToast('Lỗi khi reset asset', false);
        } finally {
            setResetingKey(null);
        }
    };

    const loadOtpEmail = async () => {
        try {
            const res = await fetch(`${API}/admin/system/otp-email`, { headers: headers() });
            const data = await res.json();
            if (data.success) {
                setOtpEmail(data.email);
            }
        } catch {
            showToast('Không thể tải email OTP hệ thống', false);
        }
    };

    const handleRequestChangeOtpEmail = async () => {
        setSendingOtpEmail(true);
        try {
            const res = await fetch(`${API}/admin/system/otp-email/request-change`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ new_email: newOtpEmail }),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setOtpEmailStep(2);
                setOtpEmailVal('');
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi gửi yêu cầu đổi email', false);
        } finally {
            setSendingOtpEmail(false);
        }
    };

    const handleConfirmChangeOtpEmail = async () => {
        setConfirmingOtpEmail(true);
        try {
            const res = await fetch(`${API}/admin/system/otp-email/confirm-change`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ new_email: newOtpEmail, otp: otpEmailVal }),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setIsEditingOtpEmail(false);
                setOtpEmail(newOtpEmail);
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi xác nhận đổi email', false);
        } finally {
            setConfirmingOtpEmail(false);
        }
    };

    const handleOpenResetModal = async (tenant: any) => {
        setSelectedTenant(tenant);
        setResetModalOpen(true);
        setResetTenantLoading(true);
        setSelectedCampaignId('');
        setOtpResetSent(false);
        setOtpResetVal('');
        try {
            const res = await fetch(`${API}/super/tenants/${tenant.id}`, { headers: headers() });
            const data = await res.json();
            if (data.success && data.data) {
                setResetTenantCampaigns(data.data.campaigns || []);
            } else {
                showToast('Không thể tải danh sách chiến dịch', false);
            }
        } catch {
            showToast('Lỗi khi tải thông tin chiến dịch của cửa hàng', false);
        } finally {
            setResetTenantLoading(false);
        }
    };

    const handleRequestResetOtp = async () => {
        if (!selectedCampaignId) return;
        setSendingOtpReset(true);
        try {
            const res = await fetch(`${API}/admin/system/reset-campaign-data/request-otp`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ campaign_id: selectedCampaignId }),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setOtpResetSent(true);
                setOtpResetVal('');
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi gửi yêu cầu OTP reset', false);
        } finally {
            setSendingOtpReset(false);
        }
    };

    const handleConfirmResetCampaign = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCampaignId || !otpResetVal) return;
        setConfirmingOtpReset(true);
        try {
            const res = await fetch(`${API}/admin/system/reset-campaign-data/confirm`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ campaign_id: selectedCampaignId, otp: otpResetVal }),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setResetModalOpen(false);
                setSelectedTenant(null);
                loadTenants(searchTerm.trim());
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi xác nhận reset dữ liệu', false);
        } finally {
            setConfirmingOtpReset(false);
        }
    };

    const handleToggleTenantStatus = async (tenant: any) => {
        const confirmMsg = tenant.status === 'active'
            ? `Bạn có chắc chắn muốn KHÓA cửa hàng "${tenant.name}"? Khi khóa, tất cả tài khoản thuộc cửa hàng này sẽ không thể đăng nhập.`
            : `Mở khóa cửa hàng "${tenant.name}"?`;
        
        if (!window.confirm(confirmMsg)) return;

        setBusy(true);
        try {
            const res = await fetch(`${API}/super/tenants/${tenant.id}/toggle-status`, {
                method: 'POST',
                headers: headers(),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                loadTenants(searchTerm.trim());
            }
        } catch {
            showToast('Lỗi kết nối máy chủ khi khóa/mở khóa', false);
        } finally {
            setBusy(false);
        }
    };

    const handleOpenTenantResetModal = (tenant: any) => {
        setSelectedTenant(tenant);
        setTenantResetModalOpen(true);
        setOtpTenantResetSent(false);
        setOtpTenantResetVal('');
    };

    const handleRequestTenantResetOtp = async () => {
        if (!selectedTenant) return;
        setSendingOtpTenantReset(true);
        try {
            const res = await fetch(`${API}/super/tenants/${selectedTenant.id}/request-reset-otp`, {
                method: 'POST',
                headers: headers(),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setOtpTenantResetSent(true);
                setOtpTenantResetVal('');
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi gửi yêu cầu OTP reset cửa hàng', false);
        } finally {
            setSendingOtpTenantReset(false);
        }
    };

    const handleConfirmTenantReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTenant || !otpTenantResetVal) return;
        setConfirmingTenantReset(true);
        try {
            const res = await fetch(`${API}/super/tenants/${selectedTenant.id}/reset-all`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ otp: otpTenantResetVal }),
            });
            const data = await res.json();
            showToast(data.message, data.success);
            if (data.success) {
                setTenantResetModalOpen(false);
                setSelectedTenant(null);
                loadTenants(searchTerm.trim());
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi xác nhận reset dữ liệu cửa hàng', false);
        } finally {
            setConfirmingTenantReset(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'infra') {
            loadStatus();
            loadOtpEmail();
        } else if (activeTab === 'tenants') {
            loadTenants();
        } else if (activeTab === 'plans') {
            loadPlans();
        } else if (activeTab === 'assets') {
            loadAssets();
        }
    }, [activeTab]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        loadTenants(searchTerm.trim());
    };

    const handleToggleTestLimits = async () => {
        setBusy(true);
        try {
            const res = await fetch(`${API}/admin/system/toggle-test-limits`, {
                method: 'POST',
                headers: headers(),
            });
            const data = await res.json();
            showToast(data.message, data.ok);
            if (data.ok) {
                await loadStatus();
            }
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi thay đổi chế độ test', false);
        } finally {
            setBusy(false);
        }
    };

    const handleClearTestData = async () => {
        const confirmText = window.prompt(
            "⚠️ CẢNH BÁO NGUY HIỂM - RESET TOÀN BỘ HỆ THỐNG!\n\nHành động này sẽ XÓA VĨNH VIỄN toàn bộ lượt chơi (Play Sessions) và Người chơi (Players) của TẤT CẢ các cửa hàng trên hệ thống.\n\nNếu bạn chắc chắn muốn thực hiện, hãy gõ chữ \"DELETE\" vào ô bên dưới để xác nhận:"
        );
        
        if (confirmText !== "DELETE") {
            if (confirmText !== null) {
                alert("Xác nhận không đúng. Đã hủy thao tác xóa dữ liệu!");
            }
            return;
        }

        setBusy(true);
        try {
            const res = await fetch(`${API}/admin/system/clear-test-data`, {
                method: 'POST',
                headers: headers(),
            });
            const data = await res.json();
            showToast(data.message, data.ok);
        } catch (err: any) {
            showToast(err.message || 'Lỗi khi xóa dữ liệu test', false);
        } finally {
            setBusy(false);
        }
    };

    const handleTestRedis = async () => {
        setBusy(true);
        setTestResult(null);
        try {
            const res  = await fetch(`${API}/admin/system/test-redis`, {
                method: 'POST', headers: headers(),
                body: JSON.stringify({ host: redisHost, port: parseInt(redisPort), password: redisPass || undefined }),
            });
            const data = await res.json();
            setTestResult({ msg: data.message, ok: data.ok });
        } catch (e: any) {
            setTestResult({ msg: e.message, ok: false });
        } finally {
            setBusy(false);
        }
    };

    const handleSwitch = async (mode: 'shared' | 'vps') => {
        if (mode === 'vps' && !testResult?.ok) {
            showToast('Hãy test Redis thành công trước khi switch sang VPS', false);
            return;
        }
        setBusy(true);
        try {
            const res  = await fetch(`${API}/admin/system/switch-mode`, {
                method: 'POST', headers: headers(),
                body: JSON.stringify({
                    mode,
                    ...(mode === 'vps' ? {
                        redis_host: redisHost,
                        redis_port: parseInt(redisPort),
                        redis_password: redisPass || undefined,
                    } : {}),
                }),
            });
            const data = await res.json();
            showToast(data.message, data.ok);
            if (data.ok) await loadStatus();
        } catch (e: any) {
            showToast(e.message, false);
        } finally {
            setBusy(false);
        }
    };

    const handleChangePlan = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTenant) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/super/tenants/${selectedTenant.id}/change-plan`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({
                    plan_code: targetPlanCode,
                    duration_days: targetPlanCode === 'free' ? undefined : parseInt(durationDays) || 30,
                    notes: notes || undefined,
                })
            });
            const data = await res.json();
            if (data.success) {
                showToast(data.message || 'Cấp gói dịch vụ thành công!', true);
                setModalOpen(false);
                setSelectedTenant(null);
                loadTenants(searchTerm);
            } else {
                showToast(data.message || 'Lỗi khi cấp gói dịch vụ', false);
            }
        } catch (err: any) {
            showToast(err.message || 'Kết nối thất bại', false);
        } finally {
            setBusy(false);
        }
    };

    const handleGivePoints = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTenant) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/admin/wallet/topup-manual`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({
                    tenant_id: selectedTenant.id,
                    amount: pointAmount,
                    type: pointType,
                    description: pointDesc,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message ?? 'Lỗi nạp xu thủ công');
            showToast(data.message ?? `Nạp thành công ${pointAmount} xu cho cửa hàng ${selectedTenant.name}!`, true);
            setPointModalOpen(false);
            setSelectedTenant(null);
            await loadTenants(searchTerm);
        } catch (err: any) {
            showToast(err.message ?? 'Lỗi kết nối khi nạp xu', false);
        } finally {
            setBusy(false);
        }
    };

    const isVps = status?.mode === 'vps';

    return (
        <Layout>
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 max-w-sm px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-start gap-2 ${
                    toast.ok ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                }`}>
                    <span className="text-base">{toast.ok ? '✅' : '❌'}</span>
                    <span>{toast.msg}</span>
                </div>
            )}

            <div className={`mx-auto px-4 py-8 space-y-6 transition-all duration-300 ${
                activeTab === 'infra' ? 'max-w-2xl' : 'max-w-6xl'
            }`}>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">🖥️ Super Admin Control Panel</h1>
                    <p className="text-sm text-gray-500 mt-1">Bảng điều khiển tối cao dành riêng cho Quản trị viên nền tảng.</p>
                </div>

                {/* Tab buttons */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('infra')}
                        className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                            activeTab === 'infra'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        🖥️ Quản lý Hạ tầng VPS
                    </button>
                    <button
                        onClick={() => setActiveTab('tenants')}
                        className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                            activeTab === 'tenants'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        🏢 Quản lý Cửa hàng & Gói cước
                    </button>
                    <button
                        onClick={() => setActiveTab('plans')}
                        className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
                            activeTab === 'plans'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        💎 Cấu hình Tính năng & Gói dịch vụ
                    </button>
                    <button
                        onClick={() => setActiveTab('assets')}
                        className={`py-3 px-6 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                            activeTab === 'assets'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        🎨 Assets Hệ Thống
                    </button>
                </div>

                {/* TAB 1: VPS INFRASTRUCTURE */}
                {activeTab === 'infra' && (
                    <div className="space-y-6">
                        {loading ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-8 animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"/>
                                <div className="h-4 bg-gray-200 rounded w-1/2"/>
                            </div>
                        ) : status && (
                            <>
                                {/* Current Mode Card */}
                                <div className={`rounded-2xl p-5 border-2 ${
                                    isVps
                                        ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300'
                                        : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Chế độ hiện tại</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                {isVps ? '🚀 VPS Mode' : '🏠 Shared Hosting Mode'}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {isVps
                                                    ? 'Cache Redis · Queue Redis · Session Redis'
                                                    : 'Cache File · Queue Database · Session File'}
                                            </p>
                                        </div>
                                        <div className="text-4xl">{isVps ? '⚡' : '🔌'}</div>
                                    </div>

                                    {/* Drivers */}
                                    <div className="bg-white rounded-xl p-4 border border-gray-100">
                                        <DriverRow label="Cache Store"      value={status.drivers.cache}   vpsValue="redis" />
                                        <DriverRow label="Session Driver"   value={status.drivers.session} vpsValue="redis" />
                                        <DriverRow label="Queue Connection" value={status.drivers.queue}   vpsValue="redis" />
                                    </div>

                                    {/* Meta */}
                                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                                        <Badge ok={status.redis_ok} text={status.redis_ok ? `Redis OK` : 'Redis chưa kết nối'} />
                                        <Badge ok={status.can_write_env} text={status.can_write_env ? '.env có thể ghi' : '.env chỉ đọc'} />
                                        <span className="text-xs text-gray-400">PHP {status.php_version}</span>
                                        <span className="text-xs text-gray-400">Laravel {status.laravel_version}</span>
                                    </div>
                                </div>


                                {/* Reset Test Data Card */}
                                {status.app_env === 'local' && (
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-1">
                                                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
                                                    🗑️ Reset Dữ liệu Chơi Thử nghiệm (Local/Dev)
                                                </h3>
                                                <p className="text-xs text-gray-500 max-w-md">
                                                    Xóa sạch toàn bộ lượt chơi (Play Sessions) và thông tin người chơi (Players) trong cơ sở dữ liệu để nhanh chóng reset giới hạn lượt chơi/IP khi đang test game.
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleClearTestData}
                                                disabled={busy}
                                                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-semibold border border-red-200 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                            >
                                                {busy ? 'Đang xóa...' : 'Xóa Dữ liệu Test'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Email OTP Configuration Card */}
                                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-1">
                                            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
                                                ✉️ Email Nhận OTP Hệ Thống
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                Email nhận mã OTP để xác nhận Reset dữ liệu chơi thử nghiệm cho các chiến dịch.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-mono bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 font-semibold">
                                                {otpEmail || 'Đang tải...'}
                                            </span>
                                            {!isEditingOtpEmail && (
                                                <button
                                                    onClick={() => {
                                                        setIsEditingOtpEmail(true);
                                                        setNewOtpEmail('');
                                                        setOtpEmailVal('');
                                                        setOtpEmailStep(1);
                                                    }}
                                                    className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-sm font-semibold border border-blue-200 transition-colors"
                                                >
                                                    Thay Đổi
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {isEditingOtpEmail && (
                                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                                            {otpEmailStep === 1 ? (
                                                <div className="flex flex-col gap-2">
                                                    <label className="text-xs font-semibold text-gray-600">Nhập email nhận OTP mới</label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="email"
                                                            value={newOtpEmail}
                                                            onChange={e => setNewOtpEmail(e.target.value)}
                                                            placeholder="vd: new-admin@gmail.com"
                                                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                                                        />
                                                        <button
                                                            onClick={handleRequestChangeOtpEmail}
                                                            disabled={sendingOtpEmail || !newOtpEmail}
                                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                                                        >
                                                            {sendingOtpEmail ? 'Đang gửi OTP...' : 'Gửi OTP đến Email cũ'}
                                                        </button>
                                                        <button
                                                            onClick={() => setIsEditingOtpEmail(false)}
                                                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition-colors"
                                                        >
                                                            Hủy
                                                        </button>
                                                    </div>
                                                    <p className="text-[11px] text-gray-400">
                                                        * Một mã OTP gồm 6 chữ số sẽ được gửi về email hiện tại ({otpEmail}) để xác nhận sự thay đổi.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <label className="text-xs font-semibold text-gray-600">
                                                        Nhập OTP đã gửi về {otpEmail} để đổi thành {newOtpEmail}
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={otpEmailVal}
                                                            onChange={e => setOtpEmailVal(e.target.value)}
                                                            placeholder="Mã OTP 6 số"
                                                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 font-mono tracking-widest text-center"
                                                            maxLength={6}
                                                        />
                                                        <button
                                                            onClick={handleConfirmChangeOtpEmail}
                                                            disabled={confirmingOtpEmail || otpEmailVal.length !== 6}
                                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                                                        >
                                                            {confirmingOtpEmail ? 'Đang xác minh...' : 'Xác nhận Đổi'}
                                                        </button>
                                                        <button
                                                            onClick={() => setOtpEmailStep(1)}
                                                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition-colors"
                                                        >
                                                            Quay lại
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Switch to VPS */}
                                {!isVps && (
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <h2 className="text-base font-semibold text-gray-900 mb-1">🚀 Chuyển sang VPS Mode</h2>
                                        <p className="text-xs text-gray-500 mb-4">
                                            Nhập thông tin Redis của VPS. Hệ thống sẽ test kết nối trước, sau đó cập nhật .env và clear cache tự động.
                                        </p>

                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                            <div>
                                                <label className="text-xs text-gray-500 mb-1 block">Redis Host</label>
                                                <input
                                                    value={redisHost}
                                                    onChange={e => setRedisHost(e.target.value)}
                                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300"
                                                    placeholder="127.0.0.1"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 mb-1 block">Redis Port</label>
                                                <input
                                                    value={redisPort}
                                                    onChange={e => setRedisPort(e.target.value)}
                                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300"
                                                    placeholder="6379"
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <label className="text-xs text-gray-500 mb-1 block">Password (nếu có)</label>
                                            <input
                                                type="password"
                                                value={redisPass}
                                                onChange={e => setRedisPass(e.target.value)}
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300"
                                                placeholder="Để trống nếu không có"
                                            />
                                        </div>

                                        {/* Test Redis */}
                                        <button
                                            onClick={handleTestRedis}
                                            disabled={busy}
                                            className="w-full py-2.5 mb-3 rounded-xl border-2 border-purple-200 text-purple-700 font-semibold text-sm hover:bg-purple-50 transition-all disabled:opacity-50"
                                        >
                                            {busy ? '⏳ Đang test...' : '🔌 Test kết nối Redis'}
                                        </button>

                                        {testResult && (
                                            <div className={`text-sm px-4 py-3 rounded-xl mb-3 ${
                                                testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                                            }`}>
                                                {testResult.msg}
                                            </div>
                                        )}

                                        <button
                                            onClick={() => handleSwitch('vps')}
                                            disabled={busy || !testResult?.ok}
                                            className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {busy ? '⏳ Đang chuyển...' : '🚀 Chuyển sang VPS Mode'}
                                        </button>
                                        {!testResult?.ok && (
                                            <p className="text-xs text-gray-400 text-center mt-2">Hãy test Redis thành công trước</p>
                                        )}
                                    </div>
                                )}

                                {/* Switch back to Shared */}
                                {isVps && (
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <h2 className="text-base font-semibold text-gray-900 mb-1">🏠 Chuyển về Shared Hosting</h2>
                                        <p className="text-xs text-gray-500 mb-4">
                                            Cache và Queue sẽ về file/database. Dùng khi cần debug hoặc chuyển host.
                                        </p>
                                        <button
                                            onClick={() => handleSwitch('shared')}
                                            disabled={busy}
                                            className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-all disabled:opacity-50"
                                        >
                                            {busy ? '⏳ Đang chuyển...' : '🔌 Chuyển về Shared Hosting Mode'}
                                        </button>
                                    </div>
                                )}

                                {/* Info box */}
                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                                    <strong>💡 Khi nào nên chuyển VPS?</strong>
                                    <ul className="mt-2 space-y-1 text-xs">
                                        <li>• Có &gt; 5 tenant trả tiền đồng thời</li>
                                        <li>• Shared hosting bắt đầu bị chậm hoặc suspend</li>
                                        <li>• Cần bán gói <strong>Thương hiệu / Agency</strong></li>
                                    </ul>
                                    <p className="mt-2 text-xs">VPS Vietnix/Azdigi 2vCPU 4GB RAM: ~150–200k/tháng</p>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* TAB 2: TENANTS & PLANS MANAGEMENT */}
                {activeTab === 'tenants' && (
                    <div className="space-y-6">
                        {/* Search bar */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                            <form onSubmit={handleSearchSubmit} className="flex gap-3">
                                <div className="flex-1 relative">
                                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 text-sm">
                                        🔍
                                    </span>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        placeholder="Tìm kiếm cửa hàng theo tên, email hoặc mã định danh..."
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    Tìm kiếm
                                </button>
                            </form>
                        </div>

                        {/* Tenants Table */}
                        {tenantsLoading ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center animate-pulse space-y-4">
                                <div className="h-6 bg-gray-200 rounded w-1/4 mx-auto" />
                                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
                                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
                            </div>
                        ) : tenants.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                                <span className="text-4xl block mb-3">🏢</span>
                                <h3 className="text-base font-bold text-gray-800">Không tìm thấy cửa hàng nào</h3>
                                <p className="text-sm text-gray-400 mt-1">Vui lòng thử tìm kiếm lại với từ khóa khác.</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                <th className="py-4 px-5">Tên Cửa Hàng & Chủ Sở Hữu</th>
                                                <th className="py-4 px-5">Mã Định Danh</th>
                                                <th className="py-4 px-5 text-center">Hoạt động / Thành viên</th>
                                                <th className="py-4 px-5">Gói Dịch Vụ</th>
                                                <th className="py-4 px-5">Hạn Sử Dụng</th>
                                                <th className="py-4 px-5 text-right">Hành Động</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 text-sm">
                                            {tenants.map(tenant => {
                                                const plan = planColors[tenant.plan_code] || planColors.free;
                                                const expiresDate = tenant.expires_at ? new Date(tenant.expires_at).toLocaleDateString('vi-VN') : 'Vô hạn';

                                                return (
                                                    <tr key={tenant.id} className="hover:bg-gray-50/50 transition-colors">
                                                        <td className="py-4 px-5">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-gray-900">{tenant.name}</span>
                                                                {tenant.status !== 'active' && (
                                                                    <span className="bg-red-100 text-red-700 text-[10px] px-2.5 py-0.5 rounded-full font-extrabold uppercase border border-red-200">
                                                                        🔒 Đã khóa
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-400 font-mono mt-0.5">{tenant.owner_email}</div>
                                                        </td>
                                                        <td className="py-4 px-5">
                                                            <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600 font-mono">{tenant.code}</code>
                                                        </td>
                                                        <td className="py-4 px-5 text-center">
                                                            <span className="inline-flex flex-col items-center">
                                                                <span className="font-semibold text-gray-800">⚡ {tenant.active_campaigns}/{tenant.max_active_campaigns === -1 ? '∞' : tenant.max_active_campaigns} Active</span>
                                                                 <span className="text-[11px] text-gray-500 block" title="Chiến dịch đã tạo / Tổng tối đa">📁 {tenant.total_campaigns}/{tenant.max_campaigns === -1 ? '∞' : tenant.max_campaigns} Total</span>
                                                                <span className="text-[10px] text-gray-400 mt-0.5">{tenant.user_count} Thành viên</span>
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-5">
                                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${plan.bg}`}>
                                                                <span>{plan.icon}</span> {plan.label}
                                                            </span>
                                                            <div className="text-xs font-bold text-indigo-650 flex items-center gap-1 mt-1">
                                                                🪙 {tenant.wallet_balance !== undefined ? tenant.wallet_balance.toLocaleString('vi-VN') : 0} xu
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-5">
                                                            {tenant.plan_code === 'free' ? (
                                                                <span className="text-gray-400">Không thời hạn</span>
                                                            ) : (
                                                                <div>
                                                                    <div className="font-medium text-gray-855">{expiresDate}</div>
                                                                    {tenant.days_remaining !== null && (
                                                                        <div className={`text-[10px] font-bold mt-0.5 ${tenant.days_remaining > 5 ? 'text-green-600' : 'text-red-500 animate-pulse'}`}>
                                                                            Còn {tenant.days_remaining} ngày
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-5 text-right flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => handleToggleTenantStatus(tenant)}
                                                                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 hover:-translate-y-0.5 cursor-pointer ${
                                                                    tenant.status === 'active'
                                                                        ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white'
                                                                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                                                                }`}
                                                            >
                                                                {tenant.status === 'active' ? '🔒 Khóa shop' : '🔓 Mở khóa'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenTenantResetModal(tenant)}
                                                                className="px-3.5 py-2 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-650 hover:to-rose-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 hover:-translate-y-0.5 cursor-pointer"
                                                            >
                                                                🗑️ Reset dữ liệu
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedTenant(tenant);
                                                                    setPointAmount(100);
                                                                    setPointType('bonus');
                                                                    setPointDesc('Thưởng điểm hệ thống (Super Admin)');
                                                                    setPointModalOpen(true);
                                                                }}
                                                                className="px-3.5 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 hover:-translate-y-0.5 cursor-pointer"
                                                            >
                                                                🪙 Cho điểm
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedTenant(tenant);
                                                                    setTargetPlanCode(tenant.plan_code);
                                                                    // Set default days based on tenant plan code
                                                                    const code = tenant.plan_code;
                                                                    if (code === 'flash_3d') setDurationDays('3');
                                                                    else if (code === 'mini_7d') setDurationDays('7');
                                                                    else if (code === 'event_15d') setDurationDays('15');
                                                                    else if (code === 'brand_30d' || code === 'agency_30d') setDurationDays('30');
                                                                    else setDurationDays('30');
                                                                    setModalOpen(true);
                                                                }}
                                                                className="px-3.5 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 hover:-translate-y-0.5"
                                                            >
                                                                👑 Cấp gói
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    localStorage.setItem("impersonated_tenant_id", tenant.id);
                                                                    localStorage.setItem("impersonated_tenant_name", tenant.name);
                                                                    window.location.href = "/dashboard";
                                                                }}
                                                                className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 hover:-translate-y-0.5"
                                                            >
                                                                👤 Đóng vai
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 3: PACKAGES & FEATURES MANAGEMENT */}
                {activeTab === 'plans' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                            <div>
                                <h3 className="text-base font-bold text-gray-800">💎 Cấu hình tham số của Gói cước</h3>
                                <p className="text-xs text-gray-400 mt-0.5">Thay đổi giá cả, số lượt chơi, các tính năng đi kèm của từng gói dịch vụ.</p>
                            </div>
                            <button
                                onClick={handleSavePlans}
                                disabled={plansSaving || !plans}
                                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-extrabold hover:shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {plansSaving ? (
                                    <>⏳ Đang lưu...</>
                                ) : (
                                    <>💾 Lưu toàn bộ gói cước</>
                                )}
                            </button>
                        </div>

                        {plansLoading || !plans ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center animate-pulse space-y-4">
                                <div className="h-6 bg-gray-200 rounded w-1/4 mx-auto" />
                                <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {Object.keys(plans).map((planKey) => {
                                    const plan = plans[planKey];
                                    return (
                                        <div key={planKey} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col justify-between">
                                            {/* Header */}
                                            <div className="bg-gray-50/70 border-b border-gray-150 p-4 flex items-center justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-extrabold text-gray-800 text-base">{plan.label}</span>
                                                        <code className="text-[10px] font-mono bg-gray-200 text-gray-600 px-2 py-0.5 rounded font-bold">{plan.code}</code>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-0.5 font-medium">{plan.subtitle}</p>
                                                </div>
                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase bg-${plan.badge_color === 'gray' ? 'gray' : plan.badge_color}-100 text-${plan.badge_color === 'gray' ? 'gray' : plan.badge_color}-800`}>
                                                    {plan.badge_color}
                                                </span>
                                            </div>

                                            {/* Body */}
                                            <div className="p-5 space-y-4 flex-1">
                                                {/* Price & Duration */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Giá bán (VND)</label>
                                                        <input
                                                            type="number"
                                                            value={plan.price}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: { ...prev![planKey], price: val }
                                                                }));
                                                            }}
                                                            className="w-full px-3.5 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm font-semibold"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Thời hạn (Ngày)</label>
                                                        <input
                                                            type="number"
                                                            value={plan.duration_days ?? ''}
                                                            placeholder="Vĩnh viễn"
                                                            onChange={(e) => {
                                                                const val = e.target.value === '' ? null : parseInt(e.target.value) || 0;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: { ...prev![planKey], duration_days: val }
                                                                }));
                                                            }}
                                                            className="w-full px-3.5 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm font-semibold"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Core Usage Quotas */}
                                                <div className="grid grid-cols-4 gap-2.5">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Tổng chiến dịch</label>
                                                        <input
                                                            type="number"
                                                            value={plan.features.max_campaigns}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: {
                                                                        ...prev![planKey],
                                                                        features: { ...prev![planKey].features, max_campaigns: val }
                                                                    }
                                                                }));
                                                            }}
                                                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg outline-none text-xs font-semibold font-mono"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Chiến dịch chạy</label>
                                                        <input
                                                            type="number"
                                                            value={plan.features.max_active_campaigns}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: {
                                                                        ...prev![planKey],
                                                                        features: { ...prev![planKey].features, max_active_campaigns: val }
                                                                     }
                                                                 }));
                                                             }}
                                                             className="w-full px-3 py-1.5 border border-gray-200 rounded-lg outline-none text-xs font-semibold font-mono"
                                                         />
                                                     </div>
                                                     <div>
                                                         <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Lượt chơi/ngày</label>
                                                         <input
                                                             type="number"
                                                             value={plan.features.max_plays_day ?? -1}
                                                             onChange={(e) => {
                                                                 const val = parseInt(e.target.value) || 0;
                                                                 setPlans(prev => ({
                                                                     ...prev!,
                                                                     [planKey]: {
                                                                         ...prev![planKey],
                                                                         features: { ...prev![planKey].features, max_plays_day: val }
                                                                     }
                                                                 }));
                                                             }}
                                                             className="w-full px-3 py-1.5 border border-gray-200 rounded-lg outline-none text-xs font-semibold font-mono"
                                                         />
                                                     </div>
                                                     <div>
                                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Lượt chơi/tháng</label>
                                                        <input
                                                            type="number"
                                                            value={plan.features.max_plays_month}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: {
                                                                        ...prev![planKey],
                                                                        features: { ...prev![planKey].features, max_plays_month: val }
                                                                    }
                                                                }));
                                                            }}
                                                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg outline-none text-xs font-semibold font-mono"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Advanced Capabilities (Checkboxes) */}
                                                <div className="border-t border-gray-100 pt-3 space-y-2.5">
                                                    <div className="text-[11px] font-bold text-gray-450 uppercase">Tính năng đặc quyền</div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!plan.features.can_export}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setPlans(prev => ({
                                                                        ...prev!,
                                                                        [planKey]: {
                                                                            ...prev![planKey],
                                                                            features: { ...prev![planKey].features, can_export: checked }
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                            />
                                                            📥 Xuất CSV leads
                                                        </label>

                                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!plan.features.custom_form}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setPlans(prev => ({
                                                                        ...prev!,
                                                                        [planKey]: {
                                                                            ...prev![planKey],
                                                                            features: { ...prev![planKey].features, custom_form: checked }
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                            />
                                                            📋 Form tuỳ chỉnh
                                                        </label>

                                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!plan.features.webhook}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setPlans(prev => ({
                                                                        ...prev!,
                                                                        [planKey]: {
                                                                            ...prev![planKey],
                                                                            features: { ...prev![planKey].features, webhook: checked }
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                            />
                                                            🔗 Tích hợp Webhook
                                                        </label>

                                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!plan.features.remove_watermark}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setPlans(prev => ({
                                                                        ...prev!,
                                                                        [planKey]: {
                                                                            ...prev![planKey],
                                                                            features: { ...prev![planKey].features, remove_watermark: checked }
                                                                        }
                                                                    }));
                                                                }}
                                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                            />
                                                            🚫 Bỏ watermark
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* Allowed Game Types */}
                                                <div className="border-t border-gray-100 pt-3 space-y-2">
                                                    <div className="text-[11px] font-bold text-gray-455 uppercase">Trò chơi được hỗ trợ (Game Types)</div>
                                                    <div className="flex flex-wrap gap-3">
                                                        {[
                                                            { code: 'lucky_wheel', label: '🎡 Vòng quay' },
                                                            { code: 'flip_card', label: '🃏 Lật bài' },
                                                            { code: 'scratch_card', label: '🎫 Thẻ cào' },
                                                            { code: 'egg_pick', label: '🥚 Đập trứng' }
                                                        ].map(gt => {
                                                            const isChecked = plan.features.game_types?.includes(gt.code);
                                                            return (
                                                                <label key={gt.code} className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 cursor-pointer bg-gray-50 border border-gray-150 px-2 py-1.5 rounded-lg hover:bg-gray-100">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            const checked = e.target.checked;
                                                                            let updatedGameTypes = [...(plan.features.game_types || [])];
                                                                            if (checked) {
                                                                                if (!updatedGameTypes.includes(gt.code)) {
                                                                                    updatedGameTypes.push(gt.code);
                                                                                }
                                                                            } else {
                                                                                updatedGameTypes = updatedGameTypes.filter(code => code !== gt.code);
                                                                            }
                                                                            setPlans(prev => ({
                                                                                ...prev!,
                                                                                [planKey]: {
                                                                                    ...prev![planKey],
                                                                                    features: { ...prev![planKey].features, game_types: updatedGameTypes }
                                                                                }
                                                                            }));
                                                                        }}
                                                                        className="w-3.5 h-3.5 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                                    />
                                                                    {gt.label}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Trạng thái hiển thị gói cước */}
                                                <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                                                    <span className="text-[11px] font-bold text-gray-455 uppercase">Trạng thái gói</span>
                                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 cursor-pointer bg-gray-50 border border-gray-150 px-3 py-1.5 rounded-lg hover:bg-gray-100 select-none transition-all active:scale-[0.98]">
                                                        <input
                                                            type="checkbox"
                                                            checked={plan.active !== false}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setPlans(prev => ({
                                                                    ...prev!,
                                                                    [planKey]: { ...prev![planKey], active: checked }
                                                                }));
                                                            }}
                                                            className="w-3.5 h-3.5 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                                        />
                                                        {plan.active !== false ? '🟢 Đang hiển thị (On)' : '🔴 Đã ẩn (Off)'}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 4: SYSTEM ASSETS */}
                {activeTab === 'assets' && (
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-2xl shadow-md flex-shrink-0">
                                    🎨
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Assets Mặc Định Hệ Thống</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Thay thế icon, watermark và background mặc định cho toàn bộ hệ thống.
                                        Các assets này sẽ được dùng khi chiến dịch <strong>chưa có custom assets</strong> riêng.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                                <span className="text-base">⚠️</span>
                                <span>Thay đổi assets hệ thống sẽ ảnh hưởng đến <strong>tất cả chiến dịch</strong> chưa upload ảnh riêng. Hãy chọn ảnh chất lượng cao và phù hợp thương hiệu.</span>
                            </div>
                        </div>

                        {/* Asset Grid */}
                        {assetsLoading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse space-y-3">
                                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                                        <div className="aspect-video bg-gray-100 rounded-xl" />
                                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {assets.map((asset) => {
                                    const meta = ASSET_META[asset.key] || { label: asset.label, icon: '🖼️', hint: '', maxKb: 2048 };
                                    const isUploading = uploadingKey === asset.key;
                                    const isReseting  = resetingKey  === asset.key;

                                    return (
                                        <div key={asset.key} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                                            {/* Card Header */}
                                            <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center gap-2">
                                                <span className="text-lg">{meta.icon}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-gray-800 truncate">{meta.label}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono">{asset.key}</p>
                                                </div>
                                                {asset.value && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                                                        ✓ Đã tuỳ chỉnh
                                                    </span>
                                                )}
                                            </div>

                                            {/* Preview */}
                                            <div className="p-4 flex-1">
                                                <div className={`relative rounded-xl overflow-hidden border-2 border-dashed mb-3 flex items-center justify-center bg-gray-50 ${
                                                    asset.key === 'default_icon' || asset.key === 'watermark'
                                                        ? 'aspect-video'
                                                        : 'aspect-video'
                                                }`}
                                                    style={{ minHeight: '120px' }}
                                                >
                                                    {asset.value ? (
                                                        <img
                                                            src={asset.value}
                                                            alt={meta.label}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    ) : (
                                                        <div className="text-center text-gray-300 select-none">
                                                            <div className="text-4xl mb-1">{meta.icon}</div>
                                                            <p className="text-xs font-medium text-gray-400">Chưa có ảnh tùy chỉnh</p>
                                                            <p className="text-[10px] text-gray-300">Dùng ảnh mặc định hệ thống</p>
                                                        </div>
                                                    )}
                                                    {isUploading && (
                                                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                                            <div className="flex flex-col items-center gap-2">
                                                                <div className="w-8 h-8 border-3 border-violet-500 border-t-transparent rounded-full animate-spin" style={{borderWidth:'3px'}} />
                                                                <span className="text-xs text-violet-600 font-semibold">Đang upload...</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Meta info */}
                                                <p className="text-[11px] text-gray-400 mb-3">{meta.hint}</p>
                                                {asset.value && (
                                                    <div className="text-[10px] text-gray-400 mb-3 space-y-0.5">
                                                        <div>📦 Kích thước: <strong className="text-gray-600">{asset.size_kb} KB</strong></div>
                                                        {asset.updated_by && <div>👤 Cập nhật bởi: <strong className="text-gray-600">{asset.updated_by}</strong></div>}
                                                        {asset.updated_at && <div>🕐 Lần cuối: <strong className="text-gray-600">{new Date(asset.updated_at).toLocaleString('vi-VN')}</strong></div>}
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="flex gap-2">
                                                    <label className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold cursor-pointer transition-all border ${
                                                        isUploading
                                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                            : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white border-transparent hover:from-violet-600 hover:to-purple-700 hover:-translate-y-0.5 shadow-sm hover:shadow-md'
                                                    }`}>
                                                        <input
                                                            type="file"
                                                            accept="image/jpeg,image/png,image/webp"
                                                            className="hidden"
                                                            disabled={isUploading}
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) handleUploadAsset(asset.key, file);
                                                                e.target.value = '';
                                                            }}
                                                        />
                                                        {isUploading ? '⏳ Đang xử lý...' : '📤 Upload ảnh mới'}
                                                    </label>
                                                    {asset.value && (
                                                        <button
                                                            onClick={() => handleResetAsset(asset.key, meta.label)}
                                                            disabled={isReseting || isUploading}
                                                            title="Reset về mặc định"
                                                            className="p-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 text-sm"
                                                        >
                                                            {isReseting ? '⏳' : '🗑️'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Info box */}
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                            <strong>💡 Cách hoạt động:</strong>
                            <ul className="mt-2 space-y-1 text-xs">
                                <li>• <strong>Icon mặc định</strong>: Hiển thị trên tab trình duyệt và trong game khi campaign chưa có logo riêng.</li>
                                <li>• <strong>Watermark</strong>: Xuất hiện ở góc game với độ mờ nhất định. Có thể bị tắt bởi gói <em>flash_3d</em> trở lên.</li>
                                <li>• <strong>Backgrounds</strong>: Nền mặc định mỗi game type. Campaign dùng gói <em>event_15d</em> trở lên có thể upload nền riêng.</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal cấp gói cước */}
            {modalOpen && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl border border-gray-100 max-w-md w-full shadow-2xl overflow-hidden transform transition-all">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                            <h3 className="text-lg font-extrabold flex items-center gap-2">
                                👑 Cấp Gói Dịch Vụ Mới
                            </h3>
                            <p className="text-xs text-blue-100 mt-1">
                                Cấp/nâng cấp gói cước thủ công cho cửa hàng <strong>{selectedTenant.name}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleChangePlan} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Chọn Gói Dịch Vụ
                                </label>
                                <select
                                    value={targetPlanCode}
                                    onChange={e => {
                                        const code = e.target.value;
                                        setTargetPlanCode(code);
                                        // Set default duration based on selected plan
                                        if (code === 'flash_3d') setDurationDays('3');
                                        else if (code === 'mini_7d') setDurationDays('7');
                                        else if (code === 'event_15d') setDurationDays('15');
                                        else if (code === 'brand_30d' || code === 'agency_30d') setDurationDays('30');
                                        else if (code === 'free') setDurationDays('30');
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm font-semibold text-gray-700 bg-white"
                                >
                                    <option value="free">🌱 Gói Free (Dùng thử)</option>
                                    <option value="flash_3d">⚡ Gói Flash (3 ngày)</option>
                                    <option value="mini_7d">📦 Gói Mini (7 ngày)</option>
                                    <option value="event_15d">🎉 Gói Event (15 ngày)</option>
                                    <option value="brand_30d">👑 Gói Brand (30 ngày)</option>
                                    <option value="agency_30d">🏢 Gói Agency (30 ngày)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Thời Hạn Sử Dụng (Ngày)
                                </label>
                                <input
                                    type="number"
                                    value={durationDays}
                                    onChange={e => setDurationDays(e.target.value)}
                                    placeholder="30"
                                    min="1"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm font-mono"
                                    required={targetPlanCode !== 'free'}
                                    disabled={targetPlanCode === 'free'}
                                />
                                {targetPlanCode === 'free' && (
                                    <p className="text-[10px] text-gray-400 mt-1">Gói Dùng thử mặc định là Vô hạn thời gian.</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Ghi Chú Kích Hoạt
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Kích hoạt đặc quyền, nâng cấp vip..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm resize-none"
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setModalOpen(false);
                                        setSelectedTenant(null);
                                    }}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                                >
                                    {busy ? '⏳ Đang cấp...' : '⚡ Xác nhận cấp'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal cộng điểm / tặng Xu cho Tenant */}
            {pointModalOpen && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl border border-gray-100 max-w-md w-full shadow-2xl overflow-hidden transform transition-all">
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
                            <h3 className="text-lg font-extrabold flex items-center gap-2">
                                🪙 Tặng / Cộng Xu Thủ Công
                            </h3>
                            <p className="text-xs text-emerald-100 mt-1">
                                Cộng thêm điểm tín dụng (Xu) vào ví cho cửa hàng <strong>{selectedTenant.name}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleGivePoints} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Số Xu Muốn Cộng
                                </label>
                                <input
                                    type="number"
                                    value={pointAmount}
                                    onChange={e => setPointAmount(Math.max(1, parseInt(e.target.value) || 0))}
                                    placeholder="100"
                                    min="1"
                                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 outline-none text-base font-black text-emerald-700 bg-white"
                                    required
                                />
                                {/* Quick Presets */}
                                <div className="grid grid-cols-4 gap-2 mt-2">
                                    {[50, 100, 500, 1000].map(amt => (
                                        <button
                                            key={amt}
                                            type="button"
                                            onClick={() => setPointAmount(amt)}
                                            className={`py-1 rounded-lg text-xs font-bold transition-all border ${
                                                pointAmount === amt
                                                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            +{amt.toLocaleString('vi-VN')}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Loại Giao Dịch
                                </label>
                                <select
                                    value={pointType}
                                    onChange={e => setPointType(e.target.value as 'bonus' | 'topup')}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 outline-none text-sm font-semibold text-gray-700 bg-white"
                                >
                                    <option value="bonus">🎁 Thưởng đặc biệt (Bonus)</option>
                                    <option value="topup">💰 Nạp tiền thủ công (Top-up)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Mô Tả / Lý Do Giao Dịch
                                </label>
                                <input
                                    type="text"
                                    value={pointDesc}
                                    onChange={e => setPointDesc(e.target.value)}
                                    placeholder="Thưởng đặc quyền, hỗ trợ sự kiện..."
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 outline-none text-sm text-gray-700"
                                    required
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPointModalOpen(false);
                                        setSelectedTenant(null);
                                    }}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                                >
                                    {busy ? '⏳ Đang cộng...' : '⚡ Xác nhận cộng'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Reset Dữ liệu Chiến dịch theo Tenant */}
            {resetModalOpen && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl border border-gray-100 max-w-md w-full shadow-2xl overflow-hidden transform transition-all">
                        <div className="bg-gradient-to-r from-red-600 to-rose-650 p-6 text-white">
                            <h3 className="text-lg font-extrabold flex items-center gap-2">
                                🗑️ Reset Dữ liệu Chơi Thử nghiệm
                            </h3>
                            <p className="text-xs text-red-100 mt-1">
                                Reset lượt chơi, quà tặng cho cửa hàng <strong>{selectedTenant.name}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleConfirmResetCampaign} className="p-6 space-y-4">
                            {resetTenantLoading ? (
                                <div className="py-8 text-center animate-pulse space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto" />
                                </div>
                            ) : resetTenantCampaigns.length === 0 ? (
                                <div className="py-6 text-center space-y-2">
                                    <p className="text-sm text-gray-500 font-semibold">Cửa hàng này chưa có chiến dịch nào.</p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setResetModalOpen(false);
                                            setSelectedTenant(null);
                                        }}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-colors"
                                    >
                                        Đóng
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                            Chọn Chiến Dịch Cần Reset
                                        </label>
                                        <select
                                            value={selectedCampaignId}
                                            onChange={e => {
                                                setSelectedCampaignId(e.target.value);
                                                setOtpResetSent(false);
                                                setOtpResetVal('');
                                            }}
                                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 outline-none text-sm font-semibold text-gray-700 bg-white"
                                            required
                                        >
                                            <option value="">-- Chọn chiến dịch --</option>
                                            {resetTenantCampaigns.map((camp: any) => (
                                                <option key={camp.id} value={camp.id}>
                                                    {camp.name} ({camp.slug})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedCampaignId && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                                                <span className="text-xs text-gray-500 font-semibold">Xác thực OTP Email:</span>
                                                <button
                                                    type="button"
                                                    onClick={handleRequestResetOtp}
                                                    disabled={sendingOtpReset}
                                                    className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-bold border border-blue-200 transition-colors disabled:opacity-50"
                                                >
                                                    {sendingOtpReset ? '⏳ Đang gửi...' : otpResetSent ? '🔁 Gửi lại OTP' : '✉️ Gửi mã OTP'}
                                                </button>
                                            </div>

                                            {otpResetSent && (
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                                        Nhập mã OTP 6 số (gửi tới {otpEmail ? (otpEmail.substring(0, 2) + '***@' + otpEmail.split('@')[1]) : 'email super admin'})
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={otpResetVal}
                                                        onChange={e => setOtpResetVal(e.target.value)}
                                                        placeholder="Mã OTP 6 số"
                                                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 outline-none text-base font-black tracking-widest text-center text-red-600 bg-white font-mono"
                                                        maxLength={6}
                                                        required
                                                    />
                                                    <p className="text-[10px] text-gray-400 mt-1">
                                                        * Lượt chơi, người chơi và lịch sử trúng giải sẽ bị xóa vĩnh viễn. Quà tặng sẽ được khôi phục số lượng.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setResetModalOpen(false);
                                                setSelectedTenant(null);
                                            }}
                                            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            Hủy bỏ
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={confirmingOtpReset || !otpResetSent || otpResetVal.length !== 6}
                                            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-red-650 to-rose-600 hover:from-red-700 hover:to-rose-700 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {confirmingOtpReset ? '⏳ Đang reset...' : '🗑️ Xác nhận Reset'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Reset Toàn bộ Dữ liệu Cửa hàng (Tenant) */}
            {tenantResetModalOpen && selectedTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl border border-gray-100 max-w-md w-full shadow-2xl overflow-hidden transform transition-all">
                        <div className="bg-gradient-to-r from-red-650 to-rose-700 p-6 text-white">
                            <h3 className="text-lg font-extrabold flex items-center gap-2">
                                ⚠️ Reset Toàn Bộ Cửa Hàng
                            </h3>
                            <p className="text-xs text-red-100 mt-1">
                                Reset và xóa sạch dữ liệu cửa hàng <strong>{selectedTenant.name}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleConfirmTenantReset} className="p-6 space-y-4">
                            <div className="bg-red-50 rounded-xl p-4 border border-red-100 text-xs text-red-700 space-y-2">
                                <p className="font-bold">⚠️ CẢNH BÁO NGUY HIỂM:</p>
                                <p>Hành động này sẽ <strong>XÓA VĨNH VIỄN</strong> toàn bộ:</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    <li>Mọi chiến dịch (campaigns)</li>
                                    <li>Tất cả người chơi (players) và lượt chơi (sessions)</li>
                                    <li>Lịch sử trúng giải (rewards)</li>
                                    <li>Các thiết lập trò chơi, quà tặng</li>
                                </ul>
                                <p className="font-semibold text-[10px]">Cửa hàng sẽ được đưa về trạng thái trắng ban đầu.</p>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                                    <span className="text-xs text-gray-500 font-semibold">Xác thực OTP Email:</span>
                                    <button
                                        type="button"
                                        onClick={handleRequestTenantResetOtp}
                                        disabled={sendingOtpTenantReset}
                                        className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-bold border border-blue-200 transition-colors disabled:opacity-50"
                                    >
                                        {sendingOtpTenantReset ? '⏳ Đang gửi...' : otpTenantResetSent ? '🔁 Gửi lại OTP' : '✉️ Gửi mã OTP'}
                                    </button>
                                </div>

                                {otpTenantResetSent && (
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                            Nhập mã OTP 6 số (gửi tới {otpEmail ? (otpEmail.substring(0, 2) + '***@' + otpEmail.split('@')[1]) : 'email super admin'})
                                        </label>
                                        <input
                                            type="text"
                                            value={otpTenantResetVal}
                                            onChange={e => setOtpTenantResetVal(e.target.value)}
                                            placeholder="Mã OTP 6 số"
                                            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-500 outline-none text-base font-black tracking-widest text-center text-red-600 bg-white font-mono"
                                            maxLength={6}
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setTenantResetModalOpen(false);
                                        setSelectedTenant(null);
                                    }}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    disabled={confirmingTenantReset || !otpTenantResetSent || otpTenantResetVal.length !== 6}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-red-650 to-rose-600 hover:from-red-700 hover:to-rose-700 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {confirmingTenantReset ? '⏳ Đang xóa...' : '🗑️ Xác Nhận Xóa'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
